import { useWebchatEmbedScript } from '~/hooks/webchat'
import { Share } from 'lucide-react'
import { Button, Tabs, TabsList, TabsTrigger, TabsContent, AsyncButton } from '@bpinternal/ui-kit-next'
import { useEffect, useMemo, useState } from 'react'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useCurrentRouteId } from '~/hooks/useCurrentRouteId'
import { WebchatContent } from '~/features/webchat/components/WebchatContent'
import { AdvancedSettings } from './sections/AdvancedSettings'
import { cn } from '~/utils'
import { DEFAULT_CUSTOM_STYLESHEET } from '../utils/const'
import { useLocalStore } from '~/stores'

export const getWebchatPageTitle = (routeId: string | undefined): string => {
  if (!routeId) return 'Webchat Settings'

  if (routeId.includes('/webchat/v2/identity')) return 'Bot Identity'
  if (routeId.includes('/webchat/v2/appearance')) return 'Bot Appearance'
  if (routeId.includes('/webchat/v2/deploy-settings')) return 'Deploy Settings'
  if (routeId.includes('/webchat/v2/features')) return 'Features'
  if (routeId.includes('/webchat/v2/')) return 'Webchat Settings'
  if (routeId.includes('/webchat')) return 'Webchat Settings'

  return 'Webchat Settings'
}

export function MobileWebchat({
  children,
  botId,
  workspaceId,
}: {
  children: React.ReactNode
  botId: string
  workspaceId: string
}) {
  const [activeTab, setActiveTab] = useState<'settings' | 'advanced-settings' | 'preview'>('settings')
  const routeId = useCurrentRouteId()
  const pageTitle = getWebchatPageTitle(routeId)
  const { shareableLink } = useWebchatEmbedScript({ botId, workspaceId })

  // This ensures when the route changes, the active tab is set to settings by default
  useEffect(() => {
    setActiveTab('settings')
  }, [routeId])

  const isDarkMode = useLocalStore((s) => s.theme === 'dark')

  return (
    <>
      <h1 className="sticky top-16 z-20 -mx-6 h-12 w-screen bg-background px-6 py-8 pb-12 text-2xl font-bold text-foreground lg:hidden">
        {pageTitle}
      </h1>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'settings' | 'advanced-settings' | 'preview')}
        className="flex h-full flex-col lg:hidden"
      >
        <div className={cn(`sticky top-[144px] z-10 -mx-6 ${isDarkMode ? 'bg-gray-50' : 'bg-white'} px-6`)}>
          <TabsList
            className={cn(
              'mb-2 mt-0 grid',
              pageTitle.toLocaleLowerCase().includes('features') ? 'grid-cols-3' : 'grid-cols-2'
            )}
          >
            <TabsTrigger value="settings">Settings</TabsTrigger>
            {pageTitle.toLocaleLowerCase().includes('features') && (
              <TabsTrigger value="advanced-settings">Advanced</TabsTrigger>
            )}
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="settings" className="h-full px-1 pb-20 lg:p-4">
          {children}
          <SaveChangesButton botId={botId} workspaceId={workspaceId} />
        </TabsContent>

        <TabsContent value="advanced-settings" className="px-1">
          <AdvancedSettings botId={botId} workspaceId={workspaceId} />
          <SaveChangesButton botId={botId} workspaceId={workspaceId} />
        </TabsContent>

        <TabsContent
          value="preview"
          className="-mb-5 flex-1"
          style={{
            marginRight: '-22px',
            marginLeft: '-22px',
          }}
        >
          <div
            className="relative flex w-full items-start justify-center bg-neutral-100 !pb-6"
            style={{
              backgroundImage: `
                linear-gradient(to right, ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(229, 231, 235, 0.7)'} 0.5px, transparent 1px),
                linear-gradient(to bottom, ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(229, 231, 235, 0.7)'} 0.5px, transparent 1px)
            `,
              backgroundSize: '20px 20px',
            }}
          >
            <Button
              disabled={!shareableLink}
              variant="outline"
              size="sm"
              className="absolute right-5 top-4 z-[5] gap-1"
              onClick={() => {
                window.open(shareableLink ?? '', '_blank')
              }}
            >
              <Share className="h-3 w-3" />
              Share
            </Button>
            <div className="mb-9 flex h-full min-h-[600px] w-full min-w-[324px] flex-col items-center justify-center px-4 sm:w-full">
              <WebchatContent botId={botId} workspaceId={workspaceId} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </>
  )
}

const SaveChangesButton = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])
  const routeId = useCurrentRouteId()

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)
  const _originalConfig = useConfigStore((state) => state._originalConfig)

  const botAvatar = useConfigStore((state) => state.botAvatar)
  const botName = useConfigStore((state) => state.botName)
  const botDescription = useConfigStore((state) => state.botDescription)
  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const footer = useConfigStore((state) => state.footer)
  const email = useConfigStore((state) => state.email)
  const phone = useConfigStore((state) => state.phone)
  const privacyPolicy = useConfigStore((state) => state.privacyPolicy)
  const termsOfService = useConfigStore((state) => state.termsOfService)
  const website = useConfigStore((state) => state.website)
  const color = useConfigStore((state) => state.color)
  const themeMode = useConfigStore((state) => state.themeMode)
  const variant = useConfigStore((state) => state.variant)
  const radius = useConfigStore((state) => state.radius)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const customStylesheet = useConfigStore((state) => state.customStyleSheet)
  const embeddedChatId = useConfigStore((state) => state.embeddedChatId)
  const toggleChatId = useConfigStore((state) => state.toggleChatId)
  const fabImage = useConfigStore((state) => state.fabImage)
  const proactiveMessageEnabled = useConfigStore((state) => state.proactiveMessageEnabled)
  const proactiveMessage = useConfigStore((state) => state.proactiveBubbleMessage)
  const delayTime = useConfigStore((state) => state.proactiveBubbleDelayTime)
  const triggerType = useConfigStore((state) => state.proactiveBubbleTriggerType)
  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const soundEnabled = useConfigStore((state) => state.soundEnabled)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const adminSecret = useConfigStore((state) => state.adminSecret)
  const allowedOrigins = useConfigStore((state) => state.allowedOrigins)

  const hasPendingChanges = useMemo(() => {
    if (!_originalConfig) return false

    if (routeId?.includes('/webchat/v2/identity')) {
      return (
        botAvatar !== _originalConfig.botAvatar ||
        botName !== _originalConfig.botName ||
        botDescription !== _originalConfig.botDescription ||
        composerPlaceholder !== _originalConfig.composerPlaceholder ||
        footer !== _originalConfig.footer ||
        email?.link !== _originalConfig.email?.link ||
        phone?.link !== _originalConfig.phone?.link ||
        privacyPolicy?.link !== _originalConfig.privacyPolicy?.link ||
        termsOfService?.link !== _originalConfig.termsOfService?.link ||
        website?.link !== _originalConfig.website?.link
      )
    }

    if (routeId?.includes('/webchat/v2/appearance')) {
      return (
        color !== _originalConfig.color ||
        themeMode !== _originalConfig.themeMode ||
        variant !== _originalConfig.variant ||
        radius !== _originalConfig.radius ||
        fontFamily !== _originalConfig.fontFamily ||
        headerVariant !== _originalConfig.headerVariant ||
        (customStylesheet ?? DEFAULT_CUSTOM_STYLESHEET) !==
          (_originalConfig.customStyleSheet ?? DEFAULT_CUSTOM_STYLESHEET)
      )
    }

    if (routeId?.includes('/webchat/v2/deploy-settings')) {
      return (
        embeddedChatId !== _originalConfig.embeddedChatId ||
        toggleChatId !== _originalConfig.toggleChatId ||
        fabImage !== _originalConfig.fabImage ||
        proactiveMessageEnabled !== _originalConfig.proactiveMessageEnabled ||
        proactiveMessage !== _originalConfig.proactiveBubbleMessage ||
        delayTime !== _originalConfig.proactiveBubbleDelayTime ||
        triggerType !== _originalConfig.proactiveBubbleTriggerType
      )
    }

    if (routeId?.includes('/webchat/v2/features')) {
      // Features page includes both Features and Advanced Settings tabs
      const featuresHasChanges =
        feedbackEnabled !== _originalConfig.feedbackEnabled ||
        allowFileUpload !== _originalConfig.allowFileUpload ||
        soundEnabled !== _originalConfig.soundEnabled ||
        storageLocation !== _originalConfig.storageLocation

      const advancedHasChanges =
        adminSecret !== _originalConfig.adminSecret ||
        JSON.stringify(allowedOrigins ?? []) !== JSON.stringify(_originalConfig.allowedOrigins ?? [])

      return featuresHasChanges || advancedHasChanges
    }

    return false
  }, [
    _originalConfig,
    routeId,
    botAvatar,
    botName,
    botDescription,
    composerPlaceholder,
    footer,
    email?.link,
    phone?.link,
    privacyPolicy?.link,
    termsOfService?.link,
    website?.link,
    color,
    themeMode,
    variant,
    radius,
    fontFamily,
    headerVariant,
    customStylesheet,
    embeddedChatId,
    toggleChatId,
    fabImage,
    proactiveMessageEnabled,
    proactiveMessage,
    delayTime,
    triggerType,
    feedbackEnabled,
    allowFileUpload,
    soundEnabled,
    storageLocation,
    adminSecret,
    allowedOrigins,
  ])

  return (
    <div className="fixed bottom-0 left-0 right-0 mx-auto w-full border-t bg-background p-4 lg:hidden">
      <AsyncButton
        className="h-12 w-full"
        onClick={saveConfig}
        disabled={_updating || !hasPendingChanges}
        loading={_updating}
      >
        Publish Changes
      </AsyncButton>
    </div>
  )
}
